import { yupResolver } from '@hookform/resolvers/yup';
import { useTranslation } from 'next-i18next';
import React, { useEffect } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import * as yup from 'yup';

import {
  useAddOrEditSupplierExpenses,
  useGetWorkdaySupplierCrops,
} from '@/modules/workingDayReconciliation/apiHooks';
import { yupValidationsShape } from '@/modules/workingDayReconciliation/components/CrewLevel/DetailedCard/Modal/AddOrEditModal/logic';
import ModalForm from '@/modules/workingDayReconciliation/components/CrewLevel/DetailedCard/Modal/AddOrEditModal/ModalForm';
import { IReconciliation } from '@/modules/workingDayReconciliation/types';
import { Button } from '@/shared/components/atoms';
import { useStore } from '@/shared/hooks/context/createGenericContext';
import { useModal } from '@/shared/hooks/context/modalContext/useModal';

interface Props {
  entityId: number | undefined;
  currentSupplierSummary?: IReconciliation.IGetWorkdaySummary | null;
  expenseType: IReconciliation.IModalType;
  operationType: IReconciliation.IModalOperationType;
  defaultData?: IReconciliation.IGetWorkdayBonusesTable;
}

const AddOrEditModal: React.FC<Props> = ({
  expenseType,
  entityId,
  operationType,
  currentSupplierSummary,
  defaultData,
}) => {
  const { mutate, isLoading } = useAddOrEditSupplierExpenses(
    expenseType,
    operationType
  );
  const [selectedAsset] = useStore((store) => store?.asset);

  const { data: supplierCrops } = useGetWorkdaySupplierCrops({
    entityId,
    supplierId: currentSupplierSummary?.supplierId,
    date: new Date(currentSupplierSummary?.date || ''),
  });
  const { closeModal } = useModal();

  const { t } = useTranslation('common');
  const validationSchema = yup.object().shape(yupValidationsShape(t));
  const methods = useForm<IReconciliation.IAddEditSupplierExpensesSchema>({
    resolver: yupResolver(validationSchema),
    mode: 'all',
    criteriaMode: 'firstError',
    shouldFocusError: true,
  });
  const { getValues } = methods;

  const handleSubmit = () => {
    mutate(
      {
        attachment: getValues('attachment'),
        cropId: getValues('cropId'),
        date: getValues('date'),
        jobInstanceId: getValues('jobInstanceId'),
        note: getValues('note'),
        supplierId: getValues('supplierId'),
        amount: getValues('amount'),
        bonusType: getValues('deductionType'),
        deductionType: getValues('deductionType'),
        // TODO: bounses
        fractionOfUnits: getValues('amount')?.toString(),
        entityId,
        expenseId: defaultData?.id,
        expenseType: defaultData?.type,
      },
      {
        onSuccess: () => {
          closeModal();
        },
      }
    );
  };

  useEffect(() => {
    methods.reset({
      supplierId: currentSupplierSummary?.supplierId,
      date: currentSupplierSummary?.date,
      cropId: defaultData?.cropId,
      amount:
        defaultData?.bonusType === 1 || defaultData?.deductionType === 1
          ? Number(defaultData?.fractionOfUnits)
          : defaultData?.amount,
      memberName: defaultData?.memberName,
      expenseType: defaultData?.type,
      deductionType: defaultData?.bonusType ?? defaultData?.deductionType,
      // attachment: defaultData?.attachment,
      note: defaultData?.note === 'undefined' ? '' : defaultData?.note || '',
      jobInstanceId: `${defaultData?.jobInstanceId} - ${defaultData?.jobName}`,
    });
    // setValue('expenseType', defaultData?.type);
  }, [defaultData, currentSupplierSummary, methods]);

  return (
    <form
      className='gap- flex max-h-[80vh] min-h-[30vh] w-full min-w-[30vw] max-w-5xl flex-col overflow-auto px-2 scrollbar-thin scrollbar-track-background-100 scrollbar-thumb-background-disable scrollbar-track-rounded-lg scrollbar-thumb-rounded-lg scrollbar-w-5 scrollbar-h-5'
      onSubmit={methods.handleSubmit(handleSubmit)}
    >
      <FormProvider {...methods}>
        <ModalForm
          supplierCrops={supplierCrops || []}
          edit={Boolean(defaultData)}
          suffix={
            defaultData?.bonusType === 0 || defaultData?.deductionType === 0
              ? selectedAsset?.currency
              : defaultData?.pricingMethod === 0
              ? t('daily')
              : defaultData?.measurementUnit
          }
        />
      </FormProvider>
      <Button
        id={`submit_${operationType}_${expenseType}`}
        type='submit'
        loading={isLoading}
        color='primary'
        size='md'
        className='mt-48 w-full min-w-full'
      >
        {t(operationType === 'add' ? 'add' : 'edit')}
      </Button>
    </form>
  );
};
export default AddOrEditModal;
